import React from 'react';
import { Card, CardContent, Grid, Stack, Typography, Box } from '@mui/material';
import {
    AssignmentTurnedIn as AssignmentIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Speed as SpeedIcon,
    Route as RouteIcon,
    PlayCircleFilled as PlayIcon,
    WarningAmber as WarningIcon,
    PersonPinCircle as PersonIcon,
} from '@mui/icons-material';
import { formatNumber, percentage, toNumber, formatMoney } from '../../../utils/formatters';

const KpiCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card
        elevation={0}
        sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
    >
        <CardContent>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box>
                    <Typography variant="body2" color="text.secondary">{title}</Typography>
                    <Typography variant="h5" fontWeight={900}>{value}</Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
                    )}
                </Box>
                <Box
                    sx={{
                        minWidth: 42, height: 42, borderRadius: 2,
                        display: 'grid', placeItems: 'center',
                        bgcolor: `${color}.light`, color: `${color}.contrastText`,
                    }}
                >
                    {icon}
                </Box>
            </Stack>
        </CardContent>
    </Card>
);

const ManifestTrackingKpiGrid = ({ summary = {} }) => {
    const total = toNumber(summary.total_manifests);
    const conIni = toNumber(summary.con_check_inicial);
    const conFin = toNumber(summary.con_check_final);
    const sinCheck = toNumber(summary.sin_check_completo);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Total manifiestos"
                    value={formatNumber(total)}
                    subtitle={`${formatNumber(summary.completados)} completados`}
                    icon={<AssignmentIcon />}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Check INICIAL"
                    value={`${percentage(conIni, total)}%`}
                    subtitle={`${formatNumber(conIni)} de ${formatNumber(total)}`}
                    icon={<CheckIcon />}
                    color="success"
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Check FINAL"
                    value={`${percentage(conFin, total)}%`}
                    subtitle={`${formatNumber(conFin)} de ${formatNumber(total)}`}
                    icon={<CheckIcon />}
                    color="info"
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Sin check completo"
                    value={formatNumber(sinCheck)}
                    subtitle="Manifiestos que requieren atención"
                    icon={<WarningIcon />}
                    color="warning"
                />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Km totales"
                    value={formatNumber(summary.km_totales)}
                    subtitle={`Promedio ${formatNumber(summary.km_promedio)} km`}
                    icon={<RouteIcon />}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Sesión activa"
                    value={formatNumber(summary.con_sesion_activa)}
                    subtitle="Con tracking en curso"
                    icon={<PlayIcon />}
                    color="success"
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Turno abierto"
                    value={formatNumber(summary.con_turno_abierto)}
                    subtitle="Conductor/asistente sin cierre"
                    icon={<PersonIcon />}
                    color="info"
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                    title="Km inconsistentes"
                    value={formatNumber(summary.con_km_inconsistente)}
                    subtitle="Check final < check inicial"
                    icon={<CancelIcon />}
                    color={toNumber(summary.con_km_inconsistente) > 0 ? 'error' : 'success'}
                />
            </Grid>
        </Grid>
    );
};

export default ManifestTrackingKpiGrid;